import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { SectionHeader } from "@/components/SectionHeader";
import { HIGHLIGHTS } from "@/data/portfolio";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Highlights = () => {
  return (
    <section
      id="highlights"
      data-testid="highlights-section"
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-white"
    >
      <SectionHeader
        eyebrow="03 / Highlights"
        title="The Reel"
        description="Footage and frames from across the seasons — goals, key moments, and life at the training ground."
        testId="highlights-header"
      />

      <Tabs defaultValue="videos" className="mt-14">
        <TabsList
          data-testid="highlights-tabs"
          className="bg-transparent border border-neutral-200 rounded-none p-0 h-auto inline-flex"
        >
          <TabsTrigger
            value="videos"
            data-testid="highlights-tab-videos"
            className="rounded-none px-6 py-3 text-xs font-semibold uppercase tracking-widest data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white"
          >
            Video Reel
          </TabsTrigger>
          <TabsTrigger
            value="gallery"
            data-testid="highlights-tab-gallery"
            className="rounded-none px-6 py-3 text-xs font-semibold uppercase tracking-widest data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white"
          >
            Photo Gallery
          </TabsTrigger>
        </TabsList>

        {/* Videos */}
        <TabsContent value="videos" className="mt-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {HIGHLIGHTS.videos.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`highlight-video-${i}`}
                className="group"
              >
                <div className="relative w-full aspect-video bg-neutral-100 overflow-hidden">
                  <video
                    src={v.src}
                    poster={v.poster}
                    controls
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">
                      {v.title}
                    </h3>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mt-1">
                      {v.meta}
                    </p>
                  </div>
                  <Play
                    size={18}
                    className="text-[#e63946] mt-1 shrink-0"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        {/* Gallery */}
        <TabsContent value="gallery" className="mt-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {HIGHLIGHTS.images.map((img, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                data-testid={`highlight-image-${i}`}
                className={`relative group overflow-hidden ${
                  i % 5 === 0 ? "sm:col-span-2 sm:row-span-2 aspect-[4/3]" : "aspect-[4/5]"
                }`}
              >
                <img
                  src={img.url}
                  alt={img.caption}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <figcaption className="absolute inset-0 flex items-end p-5 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-sm font-medium">
                    {img.caption}
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
};
